// @ts-nocheck
import { useState } from 'react';
import { View, Text, Pressable, StyleSheet, KeyboardAvoidingView, Platform, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { signInWithGoogle } from '@/utils/supabase';
import { BetaBanner } from '@/components/BetaBanner';
import { AccessibilityButton } from '@/components/AccessibilityButton';

export default function MentorLoginScreen() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    setStatusMessage('מתחבר עם Google...');

    try {
      const result = await signInWithGoogle();

      if (!result.success) {
        setError('לא הצלחנו להתחבר עם Google. נסה שוב.');
        setStatusMessage('');
        setLoading(false);
        return;
      }

      setStatusMessage('מעביר...');
    } catch (err) {
      console.error('Google login error:', err);
      setError('שגיאה בהתחברות עם Google');
      setStatusMessage('');
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <View style={styles.topBar}>
        <Pressable
          onPress={() => router.back()}
          style={styles.backButton}
          disabled={loading}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="חזרה לדף הבית"
          {...(Platform.OS === 'web' && {
            'aria-label': 'חזרה לדף הבית',
          } as any)}
        >
          <ArrowLeft color={theme.colors.text} size={24} aria-hidden={true} />
          <Text style={styles.backText}>חזרה</Text>
        </Pressable>
        <BetaBanner compact />
      </View>

      <View style={styles.content}>
        <Text
          style={styles.title}
          accessible={true}
          accessibilityRole="header"
          {...(Platform.OS === 'web' && {
            role: 'heading',
            'aria-level': 1,
          } as any)}
        >
          כניסה למנטורים
        </Text>

        {error ? (
          <Text
            style={styles.errorText}
            accessibilityRole="alert"
            accessibilityLiveRegion="assertive"
          >
            {error}
          </Text>
        ) : null}
        {statusMessage ? (
          <Text
            style={styles.statusText}
            accessibilityLiveRegion="polite"
          >
            {statusMessage}
          </Text>
        ) : null}

        <Pressable
          style={({ pressed }) => [
            styles.googleButton,
            pressed && styles.googleButtonPressed,
            loading && styles.googleButtonDisabled,
          ]}
          onPress={handleGoogleLogin}
          disabled={loading}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="התחבר עם Google"
          accessibilityHint="לחץ להתחברות עם חשבון Google"
          accessibilityState={{ disabled: loading }}
          {...(Platform.OS === 'web' && {
            'aria-label': 'התחבר עם Google',
          } as any)}
        >
          {loading ? (
            <ActivityIndicator color="#000" />
          ) : (
            <Text style={styles.googleButtonText}>התחבר עם Google</Text>
          )}
        </Pressable>
      </View>

      <AccessibilityButton />
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: theme.colors.background },
  topBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingTop: Platform.OS === 'ios' ? 60 : theme.spacing.lg,
    paddingBottom: theme.spacing.sm,
  },
  backButton: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.xs },
  backText: { fontSize: 16, fontFamily: theme.fonts.medium, color: theme.colors.text },
  content: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: theme.spacing.lg },
  title: { fontSize: 28, color: theme.colors.text, marginBottom: theme.spacing.xl, fontFamily: theme.fonts.bold },
  googleButton: {
    backgroundColor: '#FAF5EA',
    borderRadius: theme.borderRadius.lg,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.xl,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  googleButtonPressed: { backgroundColor: '#F5F5F5', transform: [{ scale: 0.98 }] },
  googleButtonDisabled: { opacity: 0.6 },
  googleButtonText: { fontSize: 16, fontFamily: theme.fonts.bold, color: '#1F1F1F' },
  errorText: { color: '#FF6B6B', marginBottom: theme.spacing.md, textAlign: 'center' },
  statusText: {
    color: theme.colors.primary,
    marginBottom: theme.spacing.md,
    textAlign: 'center',
    fontFamily: theme.fonts.medium,
  },
});
